import { useState } from "react";
import { Plus, Trash, Table as TableIcon } from "lucide-react";

export interface TableCell {
  content: string;
}

export interface TableData {
  headers: string[];
  rows: TableCell[][];
}

interface TableBlockProps {
  id: string;
  data?: TableData;
  onTableChange: (id: string, data: TableData) => void;
  onDelete: (id: string) => void;
}

export const TableBlock = ({ 
  id, 
  data, 
  onTableChange, 
  onDelete 
}: TableBlockProps) => {
  const [tableData, setTableData] = useState<TableData>(data || {
    headers: ['Column 1', 'Column 2', 'Column 3'],
    rows: [
      [{ content: '' }, { content: '' }, { content: '' }],
      [{ content: '' }, { content: '' }, { content: '' }],
    ]
  });

  const updateTableData = (newData: TableData) => {
    setTableData(newData);
    onTableChange(id, newData);
  };

  const handleHeaderChange = (index: number, value: string) => {
    const newHeaders = [...tableData.headers];
    newHeaders[index] = value;
    updateTableData({ ...tableData, headers: newHeaders });
  };

  const handleCellChange = (rowIndex: number, colIndex: number, value: string) => {
    const newRows = tableData.rows.map((row, rIdx) => 
      rIdx === rowIndex 
        ? row.map((cell, cIdx) => cIdx === colIndex ? { content: value } : cell)
        : row
    );
    updateTableData({ ...tableData, rows: newRows });
  };

  const addColumn = () => {
    const newHeaders = [...tableData.headers, `Column ${tableData.headers.length + 1}`];
    const newRows = tableData.rows.map(row => [...row, { content: '' }]);
    updateTableData({ headers: newHeaders, rows: newRows });
  };

  const removeColumn = (index: number) => {
    if (tableData.headers.length <= 1) return; // Keep at least one column
    const newHeaders = tableData.headers.filter((_, i) => i !== index);
    const newRows = tableData.rows.map(row => row.filter((_, i) => i !== index));
    updateTableData({ headers: newHeaders, rows: newRows });
  };

  const addRow = () => {
    const newRow = tableData.headers.map(() => ({ content: '' }));
    updateTableData({ ...tableData, rows: [...tableData.rows, newRow] });
  };

  const removeRow = (index: number) => {
    if (tableData.rows.length <= 1) return; // Keep at least one row
    const newRows = tableData.rows.filter((_, i) => i !== index);
    updateTableData({ ...tableData, rows: newRows });
  };

  return (
    <div className="group relative">
      {/* Controls */}


      <div className="overflow-x-auto border border-editor-command-border rounded-lg">
        <table className="w-full min-w-max border-collapse">
          <thead>
            {/* Header row with column names */}
            <tr className="bg-editor-hover">
              {tableData.headers.map((header, index) => (
                <th key={index} className="border border-editor-command-border p-0 min-w-[120px]">
                  <div className="flex items-center gap-1 pr-1">
                    <input
                      type="text"
                      value={header}
                      onChange={(e) => handleHeaderChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 bg-transparent text-editor-text font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder={`Header ${index + 1}`}
                    />
                    {tableData.headers.length > 1 && (
                      <button
                        onClick={() => removeColumn(index)}
                        className="p-1 hover:bg-destructive hover:text-destructive-foreground rounded text-editor-handle flex-shrink-0"
                        title="Remove column"
                      >
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </th>
              ))}
              <th className="border border-editor-command-border bg-editor-hover w-12 p-1">
                <button
                  onClick={addColumn}
                  className="p-1 hover:bg-primary hover:text-primary-foreground rounded text-editor-handle flex-shrink-0"
                  title="Add column"
                >
                  <Plus size={14} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="group/row hover:bg-editor-hover/50">
                {row.map((cell, colIndex) => (
                  <td key={colIndex} className="border border-editor-command-border p-0">
                    <textarea
                      value={cell.content}
                      onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                      className="w-full px-3 py-2 bg-transparent text-editor-text text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none min-h-[40px]"
                      placeholder="Enter text"
                      rows={1}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = 'auto';
                        target.style.height = target.scrollHeight + 'px';
                      }}
                    />
                  </td>
                ))}
                <td className="border border-editor-command-border p-2 bg-editor-hover/30 w-10">
                  {tableData.rows.length > 1 && (
                    <button
                      onClick={() => removeRow(rowIndex)}
                      className="p-1 hover:bg-destructive hover:text-destructive-foreground rounded text-editor-handle opacity-0 group-hover/row:opacity-100 transition-opacity duration-200"
                      title="Remove row"
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={tableData.headers.length + 1} className="border border-editor-command-border p-2 bg-editor-hover/30 text-center">
                <button
                  onClick={addRow}
                  className="inline-flex items-center gap-1 px-3 py-1 hover:bg-editor-focus rounded text-editor-text text-sm"
                  title="Add row"
                >
                  <Plus size={16} />
                  Add Row
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}; 